/**
 * 透视变换（单应矩阵，M4-02/M4-07 / DESIGN.md §6.5）。
 *
 * `Mat3`（engine/transform/mat3.ts）只是仿射（6 自由度，无透视项）——
 * 透视裁剪/消失点需要真正的单应矩阵（8 自由度：`u = (ax+by+c)/(gx+hy+1)`）。
 * 两者是不同的数学对象，不该共用一个类型，所以单独开一个模块。
 *
 * 4 点对应关系恰好给出 8 个线性方程、解出 8 个未知数（DLT 的最简形式，
 * 不需要奇异值分解——4 点精确对应时就是纯线性方程组）。
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** 单应矩阵的 8 个系数：`[a,b,c,d,e,f,g,h]`，i 恒为 1。 */
export type Homography = readonly [number, number, number, number, number, number, number, number];

/**
 * 解 4 点对应关系的单应矩阵：`src[i] → dst[i]`。
 *
 * 8×8 线性方程组，高斯消元求解。四点共线（退化四边形）时系数矩阵奇异，
 * 抛错而不是返回一个无意义的矩阵——静默给错误结果比拒绝执行更糟。
 */
export function solveHomography(src: readonly Point[], dst: readonly Point[]): Homography {
  if (src.length !== 4 || dst.length !== 4) {
    throw new RangeError('solveHomography 需要恰好 4 对点。');
  }
  // 8 个方程，8 个未知数 [a,b,c,d,e,f,g,h]
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]!;
    const { x: u, y: v } = dst[i]!;
    A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]);
    B.push(u);
    A.push([0, 0, 0, x, y, 1, -x * v, -y * v]);
    B.push(v);
  }
  const solution = solveLinearSystem(A, B);
  if (solution === null || solution.length !== 8) {
    throw new RangeError('四点退化（共线或重合），无法求解单应矩阵。');
  }
  return [
    solution[0]!,
    solution[1]!,
    solution[2]!,
    solution[3]!,
    solution[4]!,
    solution[5]!,
    solution[6]!,
    solution[7]!,
  ];
}

/** 高斯消元（含部分主元），8×8 足够小,不需要更复杂的数值方法。 */
function solveLinearSystem(A: number[][], b: readonly number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]!]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row]![col]!) > Math.abs(M[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(M[pivot]![col]!) < 1e-10) return null;
    [M[col], M[pivot]] = [M[pivot]!, M[col]!];

    const pivotValue = M[col]![col]!;
    for (let k = col; k <= n; k++) M[col]![k] = M[col]![k]! / pivotValue;

    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = M[row]![col]!;
      if (factor === 0) continue;
      for (let k = col; k <= n; k++) M[row]![k] = M[row]![k]! - factor * M[col]![k]!;
    }
  }
  return M.map((row) => row[n]!);
}

/** 用单应矩阵映射一个点（含透视除法）。 */
export function applyHomography(h: Homography, p: Point): Point {
  const [a, b, c, d, e, f, g, hh] = h;
  const w = g * p.x + hh * p.y + 1;
  return { x: (a * p.x + b * p.y + c) / w, y: (d * p.x + e * p.y + f) / w };
}

/**
 * 单应矩阵的逆——把 8 系数写成 3×3 齐次矩阵再求逆，比对 8 个方程重新解
 * 更直接（矩阵求逆是标准操作，3×3 伴随矩阵法足够）。
 */
export function invertHomography(h: Homography): Homography {
  const [a, b, c, d, e, f, g, hh] = h;
  // 3×3 矩阵 [[a,b,c],[d,e,f],[g,hh,1]]
  const m = [
    [a, b, c],
    [d, e, f],
    [g, hh, 1],
  ];
  const det =
    m[0]![0]! * (m[1]![1]! * m[2]![2]! - m[1]![2]! * m[2]![1]!) -
    m[0]![1]! * (m[1]![0]! * m[2]![2]! - m[1]![2]! * m[2]![0]!) +
    m[0]![2]! * (m[1]![0]! * m[2]![1]! - m[1]![1]! * m[2]![0]!);
  if (Math.abs(det) < 1e-12) throw new RangeError('单应矩阵退化，不可逆。');

  const inv = [
    [
      (m[1]![1]! * m[2]![2]! - m[1]![2]! * m[2]![1]!) / det,
      (m[0]![2]! * m[2]![1]! - m[0]![1]! * m[2]![2]!) / det,
      (m[0]![1]! * m[1]![2]! - m[0]![2]! * m[1]![1]!) / det,
    ],
    [
      (m[1]![2]! * m[2]![0]! - m[1]![0]! * m[2]![2]!) / det,
      (m[0]![0]! * m[2]![2]! - m[0]![2]! * m[2]![0]!) / det,
      (m[0]![2]! * m[1]![0]! - m[0]![0]! * m[1]![2]!) / det,
    ],
    [
      (m[1]![0]! * m[2]![1]! - m[1]![1]! * m[2]![0]!) / det,
      (m[0]![1]! * m[2]![0]! - m[0]![0]! * m[2]![1]!) / det,
      (m[0]![0]! * m[1]![1]! - m[0]![1]! * m[1]![0]!) / det,
    ],
  ];
  // 归一化让 [2][2] 恒为 1（与 solveHomography 的约定一致）
  const scale = inv[2]![2]!;
  return [
    inv[0]![0]! / scale,
    inv[0]![1]! / scale,
    inv[0]![2]! / scale,
    inv[1]![0]! / scale,
    inv[1]![1]! / scale,
    inv[1]![2]! / scale,
    inv[2]![0]! / scale,
    inv[2]![1]! / scale,
  ];
}

/**
 * 透视重采样：目标每个像素反算源坐标（逆单应矩阵），双线性插值取色。
 * CPU 实现——透视裁剪/消失点是偶发操作,不在每帧渲染路径上,不必上 wasm。
 */
export function samplePerspective(
  source: Uint8Array,
  srcWidth: number,
  srcHeight: number,
  dstWidth: number,
  dstHeight: number,
  inverse: Homography,
): Uint8Array {
  const out = new Uint8Array(dstWidth * dstHeight * 4);
  for (let y = 0; y < dstHeight; y++) {
    for (let x = 0; x < dstWidth; x++) {
      const src = applyHomography(inverse, { x: x + 0.5, y: y + 0.5 });
      const sx = src.x - 0.5;
      const sy = src.y - 0.5;
      const at = (y * dstWidth + x) * 4;
      if (sx < -0.5 || sy < -0.5 || sx > srcWidth - 0.5 || sy > srcHeight - 0.5) {
        continue; // 越界：留透明
      }
      const x0 = Math.floor(sx);
      const y0 = Math.floor(sy);
      const fx = sx - x0;
      const fy = sy - y0;
      const sample = (px: number, py: number): readonly [number, number, number, number] => {
        const cx = Math.min(srcWidth - 1, Math.max(0, px));
        const cy = Math.min(srcHeight - 1, Math.max(0, py));
        const i = (cy * srcWidth + cx) * 4;
        return [source[i]!, source[i + 1]!, source[i + 2]!, source[i + 3]!];
      };
      const p00 = sample(x0, y0);
      const p10 = sample(x0 + 1, y0);
      const p01 = sample(x0, y0 + 1);
      const p11 = sample(x0 + 1, y0 + 1);
      for (let c = 0; c < 4; c++) {
        const top = p00[c]! * (1 - fx) + p10[c]! * fx;
        const bottom = p01[c]! * (1 - fx) + p11[c]! * fx;
        out[at + c] = Math.round(top * (1 - fy) + bottom * fy);
      }
    }
  }
  return out;
}
